// Adaptador de CAMPAÑAS del manager sobre lealify-base (/campaigns/*), reemplazo de
// Firestore + esapi + envío WAHA-en-navegador. El envío es 100% server-side: el manager
// crea/edita/programa/controla; el worker de base drena la cola con jitter/warm-up/topes.
// Mapea base ↔ el modelo Campaign/SendPlan que ya consumen los componentes del manager.
import { baseApi } from './baseClient';
import { crearClienteCampanas } from '../features/campaigns/campaignsEndpoints';

// El TRANSPORTE es del manager (baseApi + BaseApiError, que deja el cuerpo en .data);
// las RUTAS vienen del módulo compartido con la agenda. Un endpoint nuevo se escribe
// una sola vez y las dos fachadas lo ven. (F5, 24-jul-2026)
const motor = crearClienteCampanas((method, path, body) => {
  const url = `/campaigns${path}`;
  if (method === 'GET') return baseApi.get(url);
  if (method === 'PUT') return baseApi.put(url, body);
  if (method === 'DELETE') return baseApi.del(url);
  return baseApi.post(url, body);
});
import type { Campaign, CampaignFormData, CampaignType, SendPlan, SendPlanStatus } from '../features/campaigns/types/campaign';

// ---- Forma cruda de base -----------------------------------------------------
interface BaseCampaignRow {
  id: number;
  name: string;
  description?: string | null;
  last_sent_at?: string | null;
  audience_type: 'all' | 'tag' | 'inactive' | 'best' | 'custom';
  audience_filter?: any;
  mode?: 'fixed' | 'dynamic';
  channel?: 'whatsapp' | 'email';
  email_subject?: string | null;
  // El backend SÍ los devuelve (lib/campaigns.js SELECT + columnas en `campaigns`,
  // email_design es enum NOT NULL con default 'simple'); faltaban solo en el tipo.
  email_design?: 'simple' | 'festive' | 'coupon' | null;
  email_highlight?: string | null;
  approved_at?: string | null;
  template_id?: number | null;
  message?: string | null;
  image_url?: string | null;
  status: 'draft' | 'scheduled' | 'running' | 'paused' | 'paused_error' | 'done' | 'cancelled';
  dispatch?: 'auto' | 'assisted';   // quién manda: el motor o una persona desde el panel
  archived_at?: string | null;   // eje de la FICHA (F2): archivar ya no descarta la cola
  pending_count?: number;        // pendientes reales, para decir el número en las confirmaciones
  msgs_per_day: number;
  window_start: string;
  window_end: string;
  weekdays: string;            // '0,1,2,3,4,5,6'
  vary_message?: number;
  variations?: string[] | null;
  sent_today?: number;
  total_queued: number;
  total_sent: number;
  total_failed: number;
  created_at: string;
  scheduled_at?: string | null;
  finished_at?: string | null;
  queue?: { pending: number; sent: number; failed: number; cancelled: number; skipped?: number };
}

// El contrato del motor vive en el módulo COMPARTIDO con la agenda
// (/root/sites/_shared/lealify-campaigns): el 24-jul-2026 escribí el mismo
// `AssistedQuota` dos veces el mismo día, uno en cada fachada.
import type { AssistedQuota } from '../features/campaigns/campaignMotorTypes';
export type { AssistedQuota, NumberPace, FuenteEnvio, LimiteEnvio } from '../features/campaigns/campaignMotorTypes';

// Resultados de una campaña de correo (GET /campaigns/:id/email-stats).
export interface EmailStats {
  sent: number;
  failed: number;
  opened: number;
  open_rate: number;      // % con un decimal, ya calculado en base
  total_opens: number;
  clicked: number;
  click_rate: number;
  first_sent_at: string | null;  // 'YYYY-MM-DD HH:mm' (texto a propósito: sin líos de TZ)
  last_sent_at: string | null;
  last_open_at: string | null;
  clicks_tracked: boolean;
}

// Aviso de FATIGA (GET /campaigns/:id/fatigue): personas de esta campaña que ya recibieron
// otra en los últimos `days` días. Informativo: el motor no frena por esto.
export interface Fatiga {
  days: number;
  total: number;
  afectados: number;
  ejemplos: Array<{ contact_id: number; nombre: string; otra_campaign_id: number; otra_nombre: string; otra_canal?: 'whatsapp' | 'email'; hace_dias: number }>;
  por_campana: Array<{ campaign_id: number; name: string; channel?: 'whatsapp' | 'email'; n: number }>;
}

export interface BaseAudience {
  audience_type: 'all' | 'tag' | 'inactive' | 'best' | 'custom';
  audience_filter: Record<string, any>;
}

// ---- Mapeos manager → base ---------------------------------------------------

// Funde el array de filtros del form en un objeto plano de claves nombradas.
// Acepta las DOS formas: [{inactiveDays:30}, …] (legacy) y [{type:'minPoints', value:14}, …]
// (la que emite CampaignForm v3+; con Object.assign a secas las claves type/value se
// pisaban entre filtros y los filtros inteligentes llegaban vacíos a base).
function mergeFilters(filters?: Array<Record<string, any>>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of Array.isArray(filters) ? filters : []) {
    if (!f || typeof f !== 'object') continue;
    if (typeof f.type === 'string' && 'value' in f) out[f.type] = f.value;
    else Object.assign(out, f);
  }
  return out;
}

// Sube la imagen del form (data URI del compresor local) al uploader oficial con el
// token corto que emite base, y devuelve su URL pública. Una URL http ya subida
// (campaña existente sin cambio de imagen) se regresa tal cual.
async function resolveImageUrl(image?: string | null): Promise<string | null> {
  if (!image) return null;
  if (/^https?:\/\//.test(image)) return image;
  if (!image.startsWith('data:')) return null;
  const blob = await (await fetch(image)).blob();
  const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
  const fd = new FormData();
  fd.append('image', blob, `campaign.${ext}`);
  const { token } = await baseApi.get<{ token: string }>('/uploads/token');
  const resp = await fetch('https://api.lealify.com/upload', {
    method: 'POST',
    headers: { 'x-upload-token': token },
    body: fd,
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok || !data?.success || !data?.url) {
    throw new Error(data?.error || 'No se pudo subir la imagen de la campaña');
  }
  return data.url.startsWith('http') ? data.url : `https://api.lealify.com${data.url}`;
}

// Traduce (tipo + filtros + ids seleccionados) del form a la audiencia de base.
// Cumpleaños se modela como custom+birthday_days (base no tiene tipo 'birthday').
export function audienceFromForm(type: CampaignType, filters?: any[], selectedClientIds?: string[]): BaseAudience {
  const f = mergeFilters(filters);
  if (type === 'all') return { audience_type: 'all', audience_filter: {} };
  if (type === 'inactive') return { audience_type: 'inactive', audience_filter: { inactive_days: Number(f.inactiveDays) || 60 } };
  if (type === 'bestClients') return { audience_type: 'best', audience_filter: { best_limit: Number(f.bestLimit ?? f.limit) || 100 } };
  if (type === 'birthday') {
    // birthdayRange = máximo de días hasta el cumple (0 = solo el día); birthdayMinDays = anticipación mínima.
    const rawMax = f.birthdayRange ?? f.birthdayDays;
    const maxDays = rawMax != null && rawMax !== '' ? Number(rawMax) : 30;
    const minDays = Math.min(maxDays, Math.max(0, Number(f.birthdayMinDays) || 0));
    const af: Record<string, any> = { birthday_days: maxDays };
    if (minDays > 0) af.birthday_min_days = minDays;
    return { audience_type: 'custom', audience_filter: af };
  }
  // custom
  const ids = (selectedClientIds || []).map((x) => Number(x)).filter(Boolean);
  if (ids.length) return { audience_type: 'custom', audience_filter: { contact_ids: ids } };
  const af: Record<string, any> = {};
  if (f.minPoints != null) af.min_points = Number(f.minPoints);
  if (f.recencyRange) { af.recency_min_days = Number(f.recencyRange.min); af.recency_max_days = Number(f.recencyRange.max); }
  if (f.birthdayMonth != null) af.birthday_month = Number(f.birthdayMonth) + 1;  // form 0-indexed → base 1-12
  if (f.activityMonths != null) af.active_months = Number(f.activityMonths);
  // Etiquetas: se mandan TODAS y el backend exige que el contacto las tenga TODAS (AND).
  // (Antes se tiraba todo menos la primera en silencio — bug doc 15-jul.)
  if (Array.isArray(f.tags) && f.tags.length) af.tags = f.tags.map(String);
  else if (f.tag) af.tag = f.tag;
  return { audience_type: 'custom', audience_filter: af };
}

// ---- Mapeo base → manager ----------------------------------------------------

// Una campaña sale de circulación si la archivaron a mano (archived_at) o si ya terminó
// su ciclo (done/cancelled). Antes solo contaba lo segundo: archivar era `cancel`, o sea
// descartar los pendientes. Desde F2 (23-jul-2026) archivar conserva la cola intacta.
const ARCHIVED = new Set(['done', 'cancelled']);
const PLAN_ACTIVE = new Set(['scheduled', 'running', 'paused', 'paused_error', 'done', 'cancelled']);

function reverseType(r: BaseCampaignRow): CampaignType {
  if (r.audience_type === 'inactive') return 'inactive';
  if (r.audience_type === 'best') return 'bestClients';
  if (r.audience_type === 'all') return 'all';
  // tag/custom → custom (birthday_days lo marca como 'birthday' a efectos de UI)
  const af = typeof r.audience_filter === 'string' ? safeJson(r.audience_filter) : (r.audience_filter || {});
  if (af && af.birthday_days != null) return 'birthday';
  return 'custom';
}

function safeJson(s: any): any { try { return JSON.parse(s); } catch { return {}; } }

// Reconstruye los filtros del form ({type,value}) desde audience_filter de base, para
// que editar una campaña rehidrate su audiencia (antes filters llegaba [] y guardar
// sin re-capturar los filtros la vaciaba).
function filtersFromAudience(af: Record<string, any>): Array<{ type: string; value: any }> {
  const out: Array<{ type: string; value: any }> = [];
  if (!af || typeof af !== 'object') return out;
  if (af.inactive_days != null) out.push({ type: 'inactiveDays', value: Number(af.inactive_days) });
  if (af.best_limit != null) out.push({ type: 'bestLimit', value: Number(af.best_limit) });
  if (af.birthday_days != null) out.push({ type: 'birthdayRange', value: String(af.birthday_days) });
  if (af.birthday_min_days != null) out.push({ type: 'birthdayMinDays', value: String(af.birthday_min_days) });
  if (af.min_points != null) out.push({ type: 'minPoints', value: Number(af.min_points) });
  if (af.active_months != null) out.push({ type: 'activityMonths', value: Number(af.active_months) });
  if (Array.isArray(af.tags) && af.tags.length) out.push({ type: 'tags', value: af.tags });
  else if (af.tag) out.push({ type: 'tags', value: [af.tag] });
  if (af.recency_min_days != null || af.recency_max_days != null) {
    out.push({ type: 'recencyRange', value: { min: af.recency_min_days ?? null, max: af.recency_max_days ?? null } });
  }
  if (af.birthday_month != null) out.push({ type: 'birthdayMonth', value: Number(af.birthday_month) - 1 }); // base 1-12 → form 0-11
  return out;
}

function toSendPlan(r: BaseCampaignRow): SendPlan | null {
  if (!PLAN_ACTIVE.has(r.status)) return null;   // draft → aún sin plan
  const weekdays = String(r.weekdays || '').split(',').map((x) => Number(x.trim())).filter((x) => !Number.isNaN(x));
  return {
    mode: 'custom',
    msgsPerDay: Number(r.msgs_per_day) || 0,
    window: { start: r.window_start, end: r.window_end, tz: '' },
    weekdays,
    status: r.status as SendPlanStatus,
    totalQueued: Number(r.total_queued) || 0,
    totalSent: Number(r.total_sent) || 0,
    totalFailed: Number(r.total_failed) || 0,
    totalSkipped: Number(r.queue?.skipped) || 0,   // dinámicas: salieron de la regla antes de enviar
    startDay: r.scheduled_at ? r.scheduled_at.slice(0, 10) : '',
    endDay: r.finished_at ? r.finished_at.slice(0, 10) : '',
    estDays: 0,
    avgIntervalMinutes: 0,
    warmup: null,
    sentTodayCount: Number(r.sent_today) || 0,
  };
}

export function mapCampaign(r: BaseCampaignRow): Campaign {
  const af = typeof r.audience_filter === 'string' ? safeJson(r.audience_filter) : (r.audience_filter || {});
  return {
    id: String(r.id),
    name: r.name || '',
    description: r.description || '',
    status: (r.archived_at || ARCHIVED.has(r.status)) ? 'archived' : 'active',
    motorStatus: r.status,                       // estado crudo: decide pausar/reanudar
    dispatch: r.dispatch === 'assisted' ? 'assisted' : 'auto',
    archivedAt: r.archived_at || null,
    pendingCount: Number(r.pending_count ?? r.queue?.pending ?? 0) || 0,
    type: reverseType(r),
    mode: r.mode === 'dynamic' ? 'dynamic' : 'fixed',
    channel: r.channel === 'email' ? 'email' : 'whatsapp',
    emailSubject: r.email_subject || '',
    emailDesign: (['simple', 'festive', 'coupon'].includes(r.email_design as string) ? r.email_design : 'simple') as Campaign['emailDesign'],
    emailHighlight: r.email_highlight || '',
    approvedAt: r.approved_at || null,
    message: r.message || '',
    filters: filtersFromAudience(af),
    createdAt: r.created_at ? new Date(r.created_at) : new Date(),
    updatedAt: r.scheduled_at ? new Date(r.scheduled_at) : (r.created_at ? new Date(r.created_at) : new Date()),
    createdBy: '',
    businessId: '',
    totalMessagesSent: Number(r.total_sent) || 0,
    lastMessageSentAt: r.last_sent_at ? new Date(r.last_sent_at) : null,
    image: r.image_url || null,
    selectedClientIds: Array.isArray(af?.contact_ids) ? af.contact_ids.map(String) : undefined,
    sendPlan: toSendPlan(r),
    varyMessage: r.vary_message == null ? true : !!r.vary_message,
    variations: Array.isArray(r.variations) ? r.variations : [],
  } as Campaign;
}

// ---- API ---------------------------------------------------------------------

export const campaignsApi = {
  // Muestra un diseño de correo con la marca REAL del tenant (galería del form).
  // POST a propósito: texto del usuario nunca va en query string (regla WAF).
  async previewEmail(input: { design: string; subject?: string; body?: string; highlight?: string }): Promise<{ html: string }> {
    return motor.previewCorreo(input);
  },

  async list(): Promise<Campaign[]> {
    const r = await motor.listar() as { campaigns: BaseCampaignRow[] };
    return (r.campaigns || []).map(mapCampaign);
  },

  async get(id: string): Promise<Campaign | null> {
    const r = await motor.obtener(id) as { campaign: BaseCampaignRow };
    return r.campaign ? mapCampaign(r.campaign) : null;
  },

  // Crea en borrador (status draft) a partir del form. Devuelve el id de base.
  async create(form: CampaignFormData): Promise<string> {
    const aud = audienceFromForm(form.type, form.filters, form.selectedClientIds);
    const image_url = await resolveImageUrl(form.image);
    const r = await motor.crear({
      name: form.name,
      message: form.message,
      channel: form.channel === 'email' ? 'email' : 'whatsapp',
      email_subject: form.channel === 'email' ? (form.emailSubject || null) : null,
      email_design: form.channel === 'email' ? (form.emailDesign || 'simple') : undefined,
      email_highlight: form.channel === 'email' ? (form.emailHighlight || null) : undefined,
      audience_type: aud.audience_type,
      audience_filter: aud.audience_filter,
      mode: form.mode === 'dynamic' ? 'dynamic' : 'fixed',
      // Sin esto el motor aplicaba su default ('auto') y NINGUNA campaña del manager podía
      // ser manual: 36 de 36 nacieron automáticas. (24-jul-2026)
      dispatch: form.dispatch === 'assisted' ? 'assisted' : 'auto',
      image_url,
    });
    return String(r.id);
  },

  // Edita la definición (solo permitido en borrador en base).
  async update(id: string, form: CampaignFormData): Promise<void> {
    const aud = audienceFromForm(form.type, form.filters, form.selectedClientIds);
    const image_url = await resolveImageUrl(form.image);
    await motor.editar(id, {
      name: form.name,
      message: form.message,
      channel: form.channel === 'email' ? 'email' : 'whatsapp',
      email_subject: form.channel === 'email' ? (form.emailSubject || null) : null,
      email_design: form.channel === 'email' ? (form.emailDesign || 'simple') : undefined,
      email_highlight: form.channel === 'email' ? (form.emailHighlight || null) : undefined,
      audience_type: aud.audience_type,
      audience_filter: aud.audience_filter,
      mode: form.mode === 'dynamic' ? 'dynamic' : 'fixed',
      dispatch: form.dispatch === 'assisted' ? 'assisted' : 'auto',
      image_url,
    });
  },

  // Guarda parámetros de envío (cadencia/ventana/días) — base los llee del registro.
  async setScheduleConfig(id: string, cfg: { msgs_per_day?: number; window_start?: string; window_end?: string; weekdays?: number[] }): Promise<void> {
    const body: Record<string, any> = {};
    if (cfg.msgs_per_day != null) body.msgs_per_day = cfg.msgs_per_day;
    if (cfg.window_start) body.window_start = cfg.window_start;
    if (cfg.window_end) body.window_end = cfg.window_end;
    if (cfg.weekdays) body.weekdays = cfg.weekdays.join(',');
    await motor.editar(id, body);
  },

  // Programa: resuelve audiencia y congela la cola. → {queued, est_days}.
  schedule(id: string): Promise<{ queued: number; est_days: number }> {
    return motor.programar(id);
  },

  pause(id: string): Promise<{ ok: boolean }> { return motor.pausar(id); },
  resume(id: string): Promise<{ ok: boolean }> { return motor.reanudar(id); },
  cancel(id: string): Promise<{ ok: boolean }> { return motor.cancelar(id); },

  // Archivar/restaurar la FICHA. No tocan la cola: enviados y pendientes quedan intactos
  // y restaurar devuelve la campaña tal como estaba. Antes "archivar" era `cancel` y
  // "reactivar" era `schedule`, o sea que el par borraba el historial y reencolaba a
  // quien ya había recibido el mensaje (probado en real el 23-jul-2026).
  archive(id: string): Promise<{ ok: boolean; paused?: boolean }> { return motor.archivar(id); },
  unarchive(id: string): Promise<{ ok: boolean }> { return motor.restaurar(id); },

  // Genera un pool de variaciones del mensaje (NO guarda). Para "Previsualizar variaciones".
  previewVariations(id: string, n = 12): Promise<{ original: string; variations: string[]; approved: string[] }> {
    return motor.variacionesPreview(id, { n });
  },
  // Aprueba/guarda el pool que verá el dueño → se usará al enviar. → { saved }.
  approveVariations(id: string, variations: string[]): Promise<{ saved: number }> {
    return motor.variacionesGuardar(id, { variations });
  },
  // Aplica variaciones a los mensajes PENDIENTES de una campaña ya lanzada (no toca enviados).
  applyVariationsToPending(id: string, variations: string[]): Promise<{ updated: number; pool_size: number }> {
    return motor.variacionesAplicarPendientes(id, { variations });
  },
  // Activa/desactiva las variaciones de la campaña (cualquier estado).
  setVaryMessage(id: string, on: boolean): Promise<{ ok: boolean; vary_message: number }> {
    return motor.variarMensaje(id, { vary_message: on ? 1 : 0 });
  },
  runNow(id: string): Promise<any> { return motor.enviarYa(id); },

  // Conteo de audiencia (preview en el form, sin congelar nada).
  async previewAudience(aud: BaseAudience): Promise<number> {
    const r = await motor.contarAudiencia(aud) as { count: number };
    return r.count || 0;
  },

  // Ritmo vigente del negocio: cuántos mensajes al día/hora permite HOY la antigüedad de su
  // número, y por qué. Desde el 24-jul-2026 la velocidad NO se elige: esto se muestra.
  async pace(): Promise<{
    day_cap: number; hour_cap: number; days: number; repaired: boolean; reason: string; hard_max_per_day: number;
    usado_hoy?: number; usado_hora?: number; disponible?: number; reserva_cumples?: number;
    desglose_hoy?: { bulk: number; tx: number; total: number; por_fuente: { fuente: string; clase: string; enviados: number }[] };
  }> {
    return motor.ritmo();
  },

  // Resultados de una campaña de CORREO (entrega/aperturas/clics desde email_log).
  // null = no es de correo o aún no hay nada que contar. `clicks_tracked=false` =
  // salió antes de que existiera el rastreo de clics (2-ago-2026): la UI oculta esa
  // línea en vez de pintar un 0% que sería mentira.
  async emailStats(id: string): Promise<EmailStats | null> {
    const r = await motor.resultadosCorreo(id) as { stats: EmailStats | null };
    return r?.stats || null;
  },

  // ── Envío ASISTIDO (24-jul-2026) ──────────────────────────────────────────
  // Lo manda el SERVIDOR de a uno cuando una persona lo pide, con el mismo texto
  // congelado, la misma imagen y los mismos topes que usa el worker. El navegador no
  // arma nada: de ahí salieron el "@marca" literal y la imagen ilegible del 23-jul en
  // la agenda. Existía solo en la agenda; el manager no tenía cómo.
  async quota(id: string): Promise<AssistedQuota> {
    return motor.cupo(id);
  },
  async sendRow(id: string, queueId: number, force = false): Promise<{ ok: boolean; shadow?: boolean; quota?: AssistedQuota }> {
    return motor.enviarFila(id, queueId, force ? { force: true } : undefined);
  },

  // Conteo de audiencia de una campaña EXISTENTE (usa su audiencia ya guardada en base).
  async previewCampaign(id: string): Promise<number> {
    const r = await motor.obtener(id) as { campaign: BaseCampaignRow };
    const c = r.campaign;
    if (!c) return 0;
    const af = typeof c.audience_filter === 'string' ? safeJson(c.audience_filter) : (c.audience_filter || {});
    return this.previewAudience({ audience_type: c.audience_type, audience_filter: af });
  },

  // El motor devuelve `first_name`/`last_name`/`phone_live` en cada fila desde siempre; el
  // panel los tiraba y enseñaba solo el teléfono. Se conservan aquí para poder decir QUIÉN.
  /** Fatiga: quién de esta campaña ya recibió OTRA hace poco (solo informa). */
  async fatiga(id: string, days?: number): Promise<Fatiga> {
    return motor.fatiga(id, days) as Promise<Fatiga>;
  },

  async queue(id: string, status?: string): Promise<Array<{ id: number; contact_id: number; phone: string; status: string; attempts: number; sent_at: string | null; detail: string | null; first_name?: string | null; last_name?: string | null; phone_live?: string | null; fatiga_campana?: string | null; fatiga_campaign_id?: number | null; fatiga_hace_dias?: number | null; fatiga_canal?: 'whatsapp' | 'email' | null }>> {
    const r = await motor.cola(id, { status }) as { queue: any[] };
    return r.queue || [];
  },

  /** A quién le tocaría si se programara ahora. Para BORRADORES: la cola nace al programar. */
  async audience(id: string, limit = 300): Promise<{
    total: number; suppressed: number;
    // Certificados sin WhatsApp que la audiencia ya no cuenta (lib/campaigns.js:430). El
    // panel lo lee para decir «N sin WhatsApp»; solo faltaba en el tipo.
    sin_whatsapp?: number;
    contacts: Array<{ contact_id: number; first_name?: string | null; last_name?: string | null; phone?: string | null; email?: string | null; last_visit_at?: string | null }>;
    // Envíos que NO están en la cola: rescatados del sistema anterior u hechos a mano.
    history?: Array<{ contact_id: number; first_name?: string | null; last_name?: string | null; phone?: string | null; sent_at?: string | null; origen?: string }>;
  }> {
    return motor.audiencia(id, limit) as any;
  },

  /** Manda a UNA persona sin programar la campaña (el servidor crea la fila que falta). */
  async sendContact(id: string, contactId: number, force = false): Promise<any> {
    return motor.enviarAContacto(id, contactId, force ? { force: true } : undefined);
  },

  /** Saca a alguien de ESTA campaña (suppress; sobrevive a los ciclos). */
  async discard(id: string, contactId: number, reason?: string): Promise<any> {
    return motor.descartar(id, { contact_id: contactId, reason: reason || null });
  },
  /** Deshace el descarte: vuelve a entrar en la campaña. */
  async undiscard(id: string, contactId: number): Promise<any> {
    return motor.recuperar(id, { contact_id: contactId });
  },
  /** Deshace un envío marcado (solo filas reales de la cola). */
  async unmarkSent(id: string, contactId: number): Promise<any> {
    return motor.desmarcarEnviada(id, { contact_id: contactId });
  },

  /** Contactos descartados a mano de esta campaña (suppress permanente, sobrevive ciclos). */
  async discarded(id: string): Promise<Array<{ contact_id: number; phone: string; reason?: string | null; first_name?: string | null; last_name?: string | null }>> {
    const r = await motor.descartados(id) as { discarded: any[] };
    return r.discarded || [];
  },
};

export default campaignsApi;
